import { FIELD_KINDS } from "../lib/palette";
import type { FieldKind, SignField, SignParty } from "../types";
import { FieldGlyph, IcoPlus } from "../ui/icons";
import { FieldList } from "./FieldList";
import { PlaceMode } from "./PlaceMode";

export function Palette({
  kind,
  partyId,
  parties,
  frozen,
  fields,
  sel,
  drawing,
  onKind,
  onSelectParty,
  onRename,
  onRenameCommit,
  onAdd,
  onRemove,
  selectedField,
  onFieldOptions,
  onPickField,
  onRenameField,
  onRequiredField,
  onDeleteField,
  onMoveField,
  onCancelDraw,
  onAddPlain,
}: {
  kind: FieldKind;
  partyId: number;
  parties: SignParty[];
  frozen: boolean;
  fields: SignField[];
  sel: number;
  drawing: FieldKind | null;
  onKind: (k: FieldKind) => void;
  onSelectParty: (id: number) => void;
  onRename: (id: number, name: string) => void;
  onRenameCommit: (id: number, name: string) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
  selectedField: SignField | null;
  onFieldOptions: (options: string[]) => void;
  onPickField: (index: number) => void;
  onRenameField: (index: number, name: string) => void;
  onRequiredField: (index: number) => void;
  onDeleteField: (index: number) => void;
  onMoveField: (from: number, to: number) => void;
  onCancelDraw: () => void;
  onAddPlain: () => void;
}) {
  const current = parties.find((p) => p.id === partyId) ?? parties[0];

  const tools = (
    <>
      <div className="sg-parties">
        <strong>Parties</strong>
        {parties.map((p) => (
          <div key={p.id} className={`sg-party${p.id === partyId ? " is-on" : ""}`}>
            <button
              type="button"
              className="sg-party__swatch"
              style={{ background: p.color }}
              aria-label={`Activer ${p.name}`}
              disabled={frozen}
              onClick={() => onSelectParty(p.id)}
            />
            <input
              value={p.name}
              disabled={frozen}
              aria-label="Nom de la partie"
              onFocus={() => onSelectParty(p.id)}
              onChange={(e) => onRename(p.id, e.target.value)}
              onBlur={(e) => onRenameCommit(p.id, e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            />
            {parties.length > 1 && !frozen ? (
              <button type="button" className="sg-iconbtn is-del" aria-label={`Retirer ${p.name}`} onClick={() => onRemove(p.id)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            ) : null}
          </div>
        ))}
        {!frozen && parties.length < 8 ? (
          <button type="button" className="sg-btn ghost sg-party__add" onClick={onAdd}>
            <IcoPlus />
            Ajouter une partie
          </button>
        ) : null}
      </div>
      {drawing && !frozen ? (
        <PlaceMode
          kind={drawing}
          color={current?.color || "#c9a8e4"}
          onCancel={onCancelDraw}
          onAddPlain={onAddPlain}
        />
      ) : (
        <div className="sg-palette__list">
          {FIELD_KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              className={`sg-palette__item is-${k.id}${kind === k.id ? " is-on" : ""}`}
              disabled={frozen}
              title={k.label}
              onClick={() => onKind(k.id)}
            >
              <em className="sg-palette__glyph" style={{ color: current?.color || "#c9a8e4" }}>
                <FieldGlyph kind={k.id} />
              </em>
              <strong>{k.label}</strong>
            </button>
          ))}
        </div>
      )}
    </>
  );

  const placed = (
    <FieldList
      fields={fields}
      parties={parties}
      sel={sel}
      frozen={frozen}
      onPick={onPickField}
      onRename={onRenameField}
      onRequired={onRequiredField}
      onDelete={onDeleteField}
      onMove={onMoveField}
    />
  );

  return (
    <aside className={`sg-palette${frozen ? " is-frozen" : ""}`} aria-label="Éléments à placer">
      <h2>Palette</h2>
      {frozen ? (
        <p className="sg-palette__lock">Document envoyé ou signé : plus d’édition des cases.</p>
      ) : (
        <p>Une couleur par partie. Les cases posées appartiennent à la partie active.</p>
      )}
      {frozen ? placed : tools}
      {frozen ? <div className="sg-palette__dim">{tools}</div> : placed}
      {selectedField?.kind === "select" && !frozen ? (
        <div className="sg-fieldopts">
          <strong>Champ de choix multiple</strong>
          <ol>
            {(selectedField.options && selectedField.options.length > 0 ? selectedField.options : ["Oui", "Non"]).map((opt, i, all) => (
              <li key={i}>
                <em>{i + 1}.</em>
                <input
                  value={opt}
                  placeholder={`Option ${i + 1}`}
                  aria-label={`Option ${i + 1}`}
                  onChange={(e) => {
                    const next = all.slice();
                    next[i] = e.target.value;
                    onFieldOptions(next);
                  }}
                />
                {all.length > 1 ? (
                  <button type="button" className="sg-iconbtn is-del" aria-label={`Retirer l’option ${i + 1}`} onClick={() => onFieldOptions(all.filter((_, j) => j !== i))}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
          <button
            type="button"
            className="sg-btn ghost sg-fieldopts__add"
            onClick={() => onFieldOptions([...(selectedField.options && selectedField.options.length > 0 ? selectedField.options : ["Oui", "Non"]), ""])}
          >
            <IcoPlus />
            Ajouter une option
          </button>
        </div>
      ) : null}
      <p className="sg-legal" style={{ color: "#c9b3d4" }}>
        {fields.length} élément{fields.length > 1 ? "s" : ""}
        {frozen
          ? parties.length > 1
            ? ` · ${parties.length} parties`
            : ""
          : ` · ${current?.name || "1re partie"}`}
      </p>
    </aside>
  );
}
